import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { SEARCH_SORT_OPTIONS, SearchSort, StatusOption } from '../../search-filters';

let nextId = 0;

/**
 * The global search's filter panel: one sort order, plus a status list on the tabs that have
 * one. Every pick applies as it is made. The search owns the state, because a change refetches.
 *
 * Native radios and checkboxes, so the keyboard behaves as it does for any form group. The
 * checked radio carries `cdkFocusInitial`, so opening the panel lands on the current order.
 */
@Component({
  selector: 'app-search-filter-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="filter-panel">
      <p class="dk-caption filter-panel__title">Filter</p>

      <fieldset class="filter-panel__group">
        <legend class="filter-panel__legend">Sort by:</legend>
        @for (option of sortOptions; track option.value) {
          <label class="dk-check">
            <input
              class="filter-panel__radio"
              type="radio"
              [name]="groupName"
              [value]="option.value"
              [checked]="sort() === option.value"
              [attr.cdkFocusInitial]="sort() === option.value ? '' : null"
              (change)="sortChange.emit(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        }
      </fieldset>

      @if (statusOptions().length > 0) {
        <fieldset class="filter-panel__group">
          <legend class="filter-panel__legend">{{ statusLegend() }}</legend>
          @for (option of statusOptions(); track option.value) {
            <label class="dk-check">
              <input
                class="dk-checkbox"
                type="checkbox"
                [checked]="statuses().includes(option.value)"
                (change)="toggleStatus(option.value)"
              />
              <span>{{ option.label }}</span>
            </label>
          }
        </fieldset>
      }
    </div>
  `,
  styleUrl: './search-filter-panel.component.scss',
})
export class SearchFilterPanelComponent {
  readonly sort = input.required<SearchSort>();
  readonly statuses = input<readonly string[]>([]);
  /** No options, no status section. */
  readonly statusOptions = input<readonly StatusOption[]>([]);
  readonly statusLegend = input('Request status:');

  readonly sortChange = output<SearchSort>();
  readonly statusesChange = output<string[]>();

  readonly sortOptions = SEARCH_SORT_OPTIONS;

  /** Unique per instance, or two panels on one page would be one radio group. */
  readonly groupName = `search-sort-${nextId++}`;

  toggleStatus(value: string): void {
    const current = this.statuses();
    this.statusesChange.emit(
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  }
}
